import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export interface AccessLevel {
  access_level: 'full' | 'view_only';
  reason?: string;
  can_edit: boolean;
  can_add_members: boolean;
  can_record_contributions: boolean;
  payment_overdue?: boolean;
  payment_due_date?: string;
  subscription_status?: string;
}

export function useAccessControl(groupId?: string) {
  const [accessLevel, setAccessLevel] = useState<AccessLevel>({
    access_level: 'view_only',
    can_edit: false,
    can_add_members: false,
    can_record_contributions: false,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        setLoading(true);
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          setAccessLevel({
            access_level: 'view_only',
            can_edit: false,
            can_add_members: false,
            can_record_contributions: false,
            reason: 'User not authenticated',
          });
          return;
        }

        const { data, error } = await supabase.rpc('get_user_access_level', {
          user_id_param: user.id,
          group_id_param: groupId || null,
        });

        if (error) throw error;

        setAccessLevel(data as AccessLevel);
      } catch (error) {
        console.error('Error checking access level:', error);
        setAccessLevel({
          access_level: 'view_only',
          can_edit: false,
          can_add_members: false,
          can_record_contributions: false,
          reason: 'Error checking access level',
        });
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, [groupId]);

  return { accessLevel, loading };
}

export async function canAddGroupMember(groupId: string): Promise<{ allowed: boolean; reason?: string; needs_upgrade?: boolean }> {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) {
      console.error('Auth error in canAddGroupMember:', authError);
      return { allowed: false, reason: `Authentication error: ${authError.message}` };
    }
    if (!user) {
      return { allowed: false, reason: 'User not authenticated. Please log out and log back in.' };
    }

    // Note: We don't check the logged-in user's subscription here because:
    // 1. The RPC function can_add_group_member checks the GROUP CREATOR's subscription
    // 2. The logged-in user might be an admin/clerk adding members on behalf of the creator
    // 3. The RPC will return the correct reason if the creator's subscription is expired

    const { data, error } = await supabase.rpc('can_add_group_member', {
      group_id_param: groupId,
      user_id_param: user.id,
    });

    if (error) {
      console.error('RPC error in canAddGroupMember:', error);
      throw error;
    }

    return {
      allowed: data.allowed === true,
      reason: data.reason,
      needs_upgrade: data.needs_upgrade === true,
    };
  } catch (error: any) {
    console.error('Error checking if can add member:', error);
    return {
      allowed: false,
      reason: error.message || 'Error checking member limit',
    };
  }
}

export async function canRecordContribution(groupId: string, month?: Date): Promise<{ allowed: boolean; reason?: string; view_only?: boolean }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { allowed: false, reason: 'User not authenticated' };
    }

    // Note: We don't check the logged-in user's subscription here because:
    // 1. The RPC function can_record_contribution checks the GROUP CREATOR's subscription
    // 2. The logged-in user might be an admin/clerk recording contributions on behalf of the creator
    // 3. The RPC will return the correct reason if the creator's subscription is expired or payment is overdue

    const { data, error } = await supabase.rpc('can_record_contribution', {
      group_id_param: groupId,
      user_id_param: user.id,
      month_param: (month || new Date()).toISOString(),
    });

    if (error) {
      console.error('RPC error in canRecordContribution:', error);
      throw error;
    }

    return {
      allowed: data.allowed === true,
      reason: data.reason,
      view_only: data.view_only === true,
    };
  } catch (error: any) {
    console.error('Error checking if can record contribution:', error);
    return {
      allowed: false,
      reason: error.message || 'Error checking contribution limit',
    };
  }
}

